import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '50vh',
          p: 3
        }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 3, md: 4 }, 
              textAlign: 'center',
              borderRadius: 2,
              maxWidth: '500px',
              width: '100%'
            }}
          >
            <ErrorOutlineIcon sx={{ 
              fontSize: { xs: 60, md: 80 }, 
              color: 'error.main', 
              mb: 2 
            }} />
            
            <Typography variant="h5" component="h2" gutterBottom sx={{ 
              fontWeight: 'bold',
              mb: 2
            }}>
              Bir Hata Oluştu
            </Typography>
            
            <Typography variant="body1" color="text.secondary" paragraph sx={{ 
              mb: 3,
              lineHeight: 1.6
            }}>
              Üzgünüz, sayfayı yüklerken beklenmeyen bir hata oluştu. 
              Lütfen sayfayı yenilemeyi deneyin.
            </Typography>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" component="pre" sx={{ 
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Alert>
            )}
            
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                sx={{ py: 1.5, px: 3 }}
              >
                Tekrar Dene
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
                sx={{ py: 1.5, px: 3 }}
              >
                Ana Sayfaya Git
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 